import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BrandAssets, BrandConfig, BrandDefinition, BrandKey } from 'brand/brand.interface';
import {
  brandThemeVariableKeys,
  getBrandDefinition,
  getDefaultBrandKey,
  normalizeBrandHost,
  resolveBrandKey,
} from 'brand/registry';

@Injectable({
  providedIn: 'root',
})
export class BrandService {
  private initialized = false;
  private activeBrandDefinition: BrandDefinition = getBrandDefinition(getDefaultBrandKey());

  initialize(): void {
    if (this.initialized) {
      return;
    }

    const currentHost = this.getCurrentHost();
    const brandKey = resolveBrandKey(currentHost, environment.brandByHost);
    this.activeBrandDefinition = getBrandDefinition(brandKey);
    this.applyBrandSideEffects();
    this.initialized = true;
  }

  get brandKey(): BrandKey {
    this.ensureInitialized();
    return this.activeBrandDefinition.key;
  }

  get brandConfig(): BrandConfig {
    this.ensureInitialized();
    return this.activeBrandDefinition.config;
  }

  get brandLogoBase64(): string {
    this.ensureInitialized();
    return this.activeBrandDefinition.logoBase64;
  }

  get brandAssets(): BrandAssets {
    this.ensureInitialized();
    return this.activeBrandDefinition.config.assets;
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      this.initialize();
    }
  }

  private getCurrentHost(): string {
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }

    return normalizeBrandHost(window.location.hostname || window.location.host || '');
  }

  private applyBrandSideEffects(): void {
    if (typeof document === 'undefined') {
      return;
    }

    this.applyThemeVariables();
    this.applyTitle();
    this.applyFavicons();
  }

  private applyThemeVariables(): void {
    const root = document.documentElement;

    brandThemeVariableKeys.forEach((variableName: string) => {
      root.style.removeProperty(variableName);
    });

    Object.entries(this.activeBrandDefinition.themeVariables).forEach(
      ([variableName, variableValue]) => {
        root.style.setProperty(variableName, variableValue);
      }
    );
  }

  private applyTitle(): void {
    document.title = this.activeBrandDefinition.config.siteTitle;
  }

  private applyFavicons(): void {
    const { faviconUrl, favicon16Url, favicon32Url } = this.activeBrandDefinition.config.assets;

    this.upsertFaviconLink(
      'app-favicon-32',
      'icon',
      favicon32Url ?? faviconUrl,
      'image/png',
      '32x32'
    );
    this.upsertFaviconLink(
      'app-favicon-16',
      'icon',
      favicon16Url ?? faviconUrl,
      'image/png',
      '16x16'
    );
    this.upsertFaviconLink(
      'app-favicon-shortcut',
      'shortcut icon',
      favicon32Url ?? faviconUrl,
      'image/png'
    );
  }

  private upsertFaviconLink(
    id: string,
    rel: string,
    href: string,
    type: string,
    sizes?: string
  ): void {
    let linkElement = document.getElementById(id) as HTMLLinkElement | null;

    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.id = id;
      document.head.appendChild(linkElement);
    }

    linkElement.rel = rel;
    linkElement.type = type;
    linkElement.href = href;

    if (sizes) {
      linkElement.setAttribute('sizes', sizes);
    } else {
      linkElement.removeAttribute('sizes');
    }
  }
}
